// @greenlight-spec: 12-azure-installation.md §IdP OIDC App Registration
// @greenlight-spec: 19-design-system.md

import * as React from 'react';

import {
  PipelineCheckRow,
  type PipelineCheckStatus,
} from '@/components/greenlight/PipelineCheckRow';
import type { IdpValidationResult } from '@greenlight/contract';

type CheckKey = keyof IdpValidationResult['checks'];

/**
 * The checks in the order an operator reads them: what the issuer publishes,
 * then what it says about the client credentials entered above it. Names are
 * the operator's vocabulary, except the three discovery endpoints, which are
 * the field names their IdP console shows.
 */
const GROUPS: ReadonlyArray<{ label: string; checks: ReadonlyArray<[CheckKey, string]> }> = [
  {
    label: 'Issuer',
    checks: [
      ['issuer', 'Issuer responds'],
      ['authorization_endpoint', 'authorization_endpoint'],
      ['token_endpoint', 'token_endpoint'],
      ['jwks_uri', 'jwks_uri'],
    ],
  },
  {
    label: 'Client registration',
    checks: [
      ['client_id_format', 'Client ID format'],
      ['client_credentials', 'Client credentials'],
      ['redirect_uri', 'Redirect URI registered'],
    ],
  },
];

export interface IdpCheckListProps {
  /** Null while the probe has not returned; `running` renders every row. */
  result: IdpValidationResult | null;
  running?: boolean;
  'data-testid'?: string;
}

/**
 * The verification checklist both write surfaces render. An inconclusive check
 * reuses the `skipped` status with its reason on the detail line, so it reads
 * as deliberately not applicable rather than pending or broken, and the
 * remediation for a failing row sits on the row itself.
 */
export function IdpCheckList({
  result,
  running = false,
  'data-testid': testId = 'idp-checks',
}: IdpCheckListProps): React.JSX.Element | null {
  if (!running && !result) return null;

  return (
    <div className="space-y-3" data-testid={testId}>
      {GROUPS.map((group) => (
        <div key={group.label} className="space-y-1.5">
          <h3 className="text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground">
            {group.label}
          </h3>
          <div className="rounded-lg border">
            {group.checks.map(([key, name]) => {
              const check = result?.checks[key];
              return (
                <PipelineCheckRow
                  key={key}
                  name={name}
                  status={rowStatus(check?.status, running)}
                  detail={check?.detail}
                  suggestedFix={check?.suggested_fix ?? null}
                  data-testid={`idp-check-${key}`}
                />
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function rowStatus(
  status: IdpValidationResult['checks'][CheckKey]['status'] | undefined,
  running: boolean,
): PipelineCheckStatus {
  if (status === undefined) return running ? 'running' : 'skipped';
  if (status === 'inconclusive') return 'skipped';
  return status;
}
